/**
 * Khối trên canvas Tự động hoá v2 — thẻ trực quan kiểu bot-builder:
 *  - nhãn tab ở góc trên trái (đổi màu theo kết quả chạy thử),
 *  - thẻ trắng: hàng đầu là biểu tượng tròn + tên loại khối, thân là xem trước nội dung
 *    (tin nhắn với biến {{…}} thành chip tím, luật điều kiện, thời gian chờ…),
 *  - nút "Bước tiếp theo ⊕" ở góc dưới phải = đầu ra (kéo dây hoặc bấm để thêm khối sau).
 * Khối Kích hoạt có dải xanh đậm + công tắc bật/tắt luồng, nút "Đổi loại kích hoạt".
 * Kèm 2 hộp chọn dạng lưới thẻ: Chọn loại kích hoạt · Chọn khối tiếp theo.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Handle, Position, type NodeProps } from '@xyflow/react'
import {
  Zap, ArrowLeftRight, GitBranch, Clock, Database, Plug, CalendarClock, MousePointerClick, PlusCircle, RefreshCw,
  Tag, Tags, MessageCircle, Send, ClipboardList, Ticket, CheckSquare, Braces, Table2, Globe, Bell,
  Users, MessagesSquare, ShoppingCart, ListTodo, CalendarDays, Store, Sheet, Webhook, Boxes, LayoutGrid, Search, Workflow, UserPlus,
  type LucideIcon,
} from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Switch } from '@/components/ui/misc'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useStoreV2 } from '@/hooks/use-automation-v2'
import {
  ACTION_V2_LABELS, CONDITION_OP_V2_LABELS, SOURCE_EVENTS, SOURCE_LABELS,
  type ActionV2, type ConditionRuleV2, type NodeKind, type SourceType,
} from '@/lib/automation-v2/types'
import { INTERNAL_OBJECTS, INTERNAL_EVENT_LABELS, TRIGGER_MODE_LABELS, type InternalEvent, type InternalObject, type TriggerMode } from '@/lib/automation-v2/kich-hoat'
import { MAU_WORKFLOW, NHOM_MAU, type MauWorkflow, type NhomMau } from '@/lib/automation-v2/mau-workflow'
import { BieuTuongBuoc } from './chi-tiet-lan-chay'

// ── Dữ liệu khối & ngữ cảnh hành vi ─────────────────────────────────
export interface BlockData extends Record<string, unknown> {
  kind: NodeKind
  label: string
  config: Record<string, unknown>
  runStatus?: 'ok' | 'fail' | 'wait' | 'skip'
  branch?: 'true' | 'false'
}

export interface CanvasHanhVi {
  /** Bấm "Bước tiếp theo" trên một khối (handle 'true'/'false' với Điều kiện). */
  themSau: (nodeId: string, handle?: 'true' | 'false') => void
  doiKichHoat: (nodeId: string) => void
  enabled: boolean
  toggle: (v: boolean) => void
}
export const CanvasCtx = createContext<CanvasHanhVi | null>(null)

// ── Bảng màu / biểu tượng ────────────────────────────────────────────
type Mau = 'blue' | 'sky' | 'amber' | 'slate' | 'emerald' | 'rose' | 'violet' | 'teal' | 'orange' | 'indigo' | 'cyan' | 'green'
const VONG: Record<Mau, string> = {
  blue: 'bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-300',
  sky: 'bg-sky-100 text-sky-600 dark:bg-sky-500/20 dark:text-sky-300',
  amber: 'bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-300',
  slate: 'bg-slate-200 text-slate-600 dark:bg-slate-500/20 dark:text-slate-300',
  emerald: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-300',
  rose: 'bg-rose-100 text-rose-600 dark:bg-rose-500/20 dark:text-rose-300',
  violet: 'bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white',
  teal: 'bg-teal-100 text-teal-600 dark:bg-teal-500/20 dark:text-teal-300',
  orange: 'bg-orange-100 text-orange-600 dark:bg-orange-500/20 dark:text-orange-300',
  indigo: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-300',
  cyan: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-500/20 dark:text-cyan-300',
  green: 'bg-green-100 text-green-600 dark:bg-green-500/20 dark:text-green-300',
}

export const KHOI_META: Record<NodeKind, { nhan: string; icon: LucideIcon; mau: Mau }> = {
  source: { nhan: 'Kích hoạt', icon: Zap, mau: 'blue' },
  mapping: { nhan: 'Ánh xạ', icon: ArrowLeftRight, mau: 'sky' },
  condition: { nhan: 'Điều kiện', icon: GitBranch, mau: 'amber' },
  delay: { nhan: 'Chờ', icon: Clock, mau: 'slate' },
  action: { nhan: 'Hành động', icon: Zap, mau: 'emerald' },
}

export type NhomHanhDong = 'khach' | 'tin' | 'noibo' | 'tichhop'
export const NHOM_LABELS: Record<NhomHanhDong | 'luong', string> = { khach: 'Khách hàng', tin: 'Tin nhắn', noibo: 'Nội bộ', tichhop: 'Tích hợp', luong: 'Điều khiển luồng' }
export const HANH_DONG_META: Record<ActionV2, { icon: LucideIcon; mau: Mau; nhom: NhomHanhDong; moTa: string }> = {
  add_tag: { icon: Tag, mau: 'emerald', nhom: 'khach', moTa: 'Gắn nhãn lên hồ sơ khách' },
  remove_tag: { icon: Tags, mau: 'rose', nhom: 'khach', moTa: 'Gỡ một nhãn khỏi khách' },
  update_property: { icon: Braces, mau: 'cyan', nhom: 'khach', moTa: 'Ghi thuộc tính CDP' },
  send_zalo_personal: { icon: MessageCircle, mau: 'blue', nhom: 'tin', moTa: 'Nick Zalo cá nhân — chỉ cần SĐT, nên sau bước kết bạn' },
  send_zalo_oa: { icon: MessageCircle, mau: 'violet', nhom: 'tin', moTa: 'Khách đã nhắn/quan tâm OA · dự phòng ZNS' },
  send_message: { icon: MessageCircle, mau: 'slate', nhom: 'tin', moTa: 'Facebook / hội thoại gần nhất' },
  send_zalo_zns: { icon: Send, mau: 'blue', nhom: 'tin', moTa: 'Mẫu ZNS đã duyệt' },
  send_survey: { icon: ClipboardList, mau: 'teal', nhom: 'tin', moTa: 'Tin kèm link form' },
  create_voucher: { icon: Ticket, mau: 'orange', nhom: 'khach', moTa: 'Sinh mã giảm giá cho khách' },
  create_task: { icon: CheckSquare, mau: 'indigo', nhom: 'noibo', moTa: 'Giao việc có hạn cho nhân viên' },
  send_notification: { icon: Bell, mau: 'amber', nhom: 'noibo', moTa: 'Báo nhóm / quản lý' },
  send_friend_request: { icon: UserPlus, mau: 'blue', nhom: 'khach', moTa: 'Mời kết bạn Zalo theo SĐT đơn hàng' },
  write_sheet: { icon: Table2, mau: 'green', nhom: 'tichhop', moTa: 'Thêm dòng vào Google Sheet' },
  http_request: { icon: Globe, mau: 'slate', nhom: 'tichhop', moTa: 'Gọi API hệ thống khác' },
}

const MODE_ICON: Record<TriggerMode, LucideIcon> = { internal: Database, external: Plug, schedule: CalendarClock, manual: MousePointerClick }
const OBJ_ICON: Record<InternalObject, LucideIcon> = { customer: Users, conversation: MessagesSquare, order: ShoppingCart, task: ListTodo, appointment: CalendarDays }
const SRC_ICON: Record<SourceType, LucideIcon> = { kiotviet: Store, gsheet: Sheet, webhook: Webhook, fmrp: Boxes, chatmql: Database, schedule: CalendarClock }
export const LICH_LABELS: Record<string, string> = {
  '0 9 * * *': 'Hằng ngày 09:00', '0 18 * * *': 'Hằng ngày 18:00', '0 9 * * 1': 'Thứ Hai 09:00', '0 8 1 * *': 'Ngày 1 hằng tháng 08:00', '*/30 * * * *': 'Mỗi 30 phút',
}

// ── Mảnh hiển thị ────────────────────────────────────────────────────
/** Văn bản có {{biến}} → chip tím (như trình dựng bot). */
export function HienBien({ text, className }: { text: string; className?: string }) {
  const parts = text.split(/(\{\{[^}]+\}\})/g)
  return (
    <span className={className}>
      {parts.map((p, i) => /^\{\{[^}]+\}\}$/.test(p)
        ? <span key={i} className="mx-px inline-block rounded bg-violet-100 px-1 font-mono text-[10.5px] leading-4 text-violet-700 dark:bg-violet-500/25 dark:text-violet-200">{p}</span>
        : <span key={i}>{p}</span>)}
    </span>
  )
}

function VongIcon({ icon: I, mau, size = 'h-9 w-9' }: { icon: LucideIcon; mau: Mau; size?: string }) {
  return <span className={cn('inline-flex shrink-0 items-center justify-center rounded-full', size, VONG[mau])}><I className="h-[45%] w-[45%]" /></span>
}

function Dong({ nhan, children }: { nhan: string; children: ReactNode }) {
  return <div className="flex items-baseline gap-2 text-[11px]"><span className="w-16 shrink-0 text-muted-foreground">{nhan}</span><span className="min-w-0 flex-1 truncate font-medium">{children}</span></div>
}

function KhungTin({ children }: { children: ReactNode }) {
  return <div className="line-clamp-4 whitespace-pre-wrap rounded-md border bg-muted/40 px-2.5 py-2 text-[12px] leading-5">{children}</div>
}

function Chip({ children, mau = 'bg-muted text-foreground' }: { children: ReactNode; mau?: string }) {
  return <span className={cn('inline-flex max-w-full items-center truncate rounded-full px-2 py-0.5 text-[10.5px] font-medium', mau)}>{children}</span>
}

function LuatChip({ r }: { r: ConditionRuleV2 }) {
  const coGiaTri = !['exists', 'not_exists'].includes(r.op)
  return (
    <div className="flex flex-wrap items-center gap-1 text-[11px]">
      <span className="rounded bg-violet-100 px-1 font-mono text-[10.5px] text-violet-700 dark:bg-violet-500/25 dark:text-violet-200">{r.field.split('.').slice(-2).join('.')}</span>
      <span className="text-muted-foreground">{CONDITION_OP_V2_LABELS[r.op]}</span>
      {coGiaTri && <span className="font-medium">“{r.value || '…'}”</span>}
    </div>
  )
}

/** Nút đầu ra "Bước tiếp theo ⊕" chứa handle nguồn của React Flow. */
function NutTiepTheo({ id, nhan, mau, onClick }: { id?: string; nhan: string; mau: string; onClick: () => void }) {
  return (
    <div className={cn('nodrag relative flex items-center gap-1 rounded-md py-1 pl-2.5 pr-3 text-[11px] font-semibold text-white shadow-sm', mau)}>
      <button type="button" className="flex items-center gap-1 hover:opacity-90" onClick={(e) => { e.stopPropagation(); onClick() }} title="Bấm để thêm khối sau, hoặc kéo chấm tròn để nối">
        {nhan} <PlusCircle className="h-3.5 w-3.5" />
      </button>
      <Handle id={id} type="source" position={Position.Right} className="!h-3 !w-3 !border-2 !border-background !bg-foreground/80 hover:!bg-foreground" />
    </div>
  )
}

// ── Thân khối theo loại ──────────────────────────────────────────────
function ThanKichHoat({ d }: { d: BlockData }) {
  const { connectors } = useStoreV2()
  const cfg = d.config
  const mode = String(cfg.mode ?? 'external') as TriggerMode
  const loc = (cfg.filterRules as ConditionRuleV2[] | undefined) ?? []
  if (mode === 'internal') {
    const o = INTERNAL_OBJECTS[cfg.object as InternalObject]; const ev = INTERNAL_EVENT_LABELS[cfg.internalEvent as InternalEvent]
    return (
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">{o && <VongIcon icon={OBJ_ICON[cfg.object as InternalObject]} mau="blue" size="h-7 w-7" />}<div className="min-w-0"><p className="truncate text-[12.5px] font-semibold">{o?.ten ?? 'Chọn đối tượng'}</p><p className="truncate text-[11px] text-muted-foreground">{ev?.ten ?? 'Chọn sự kiện'}</p></div></div>
        {cfg.internalEvent === 'field_changed' && !!cfg.field && <Dong nhan="Trường"><span className="font-mono">{String(cfg.field).split('.').pop()}</span>{!!cfg.toValue && <> → <span className="text-primary">{String(cfg.toValue)}</span></>}</Dong>}
        {loc.map((r, i) => <div key={i} className="flex items-center gap-1"><span className="text-[10.5px] text-muted-foreground">Chỉ khi</span><LuatChip r={r} /></div>)}
      </div>
    )
  }
  if (mode === 'schedule') {
    const o = INTERNAL_OBJECTS[cfg.object as InternalObject]
    return (
      <div className="space-y-1.5">
        <p className="text-[15px] font-bold tabular-nums">{LICH_LABELS[String(cfg.cron)] ?? String(cfg.cron ?? 'Chưa đặt lịch')}</p>
        <Dong nhan="Quét">{o?.ten ?? 'Khách hàng'}</Dong>
        {loc.map((r, i) => <div key={i} className="flex items-center gap-1"><span className="text-[10.5px] text-muted-foreground">Chỉ khi</span><LuatChip r={r} /></div>)}
      </div>
    )
  }
  if (mode === 'manual') {
    const noi = { customer: 'hồ sơ khách hàng', conversation: 'khung hội thoại', order: 'đơn hàng' }[String(cfg.object ?? 'customer')] ?? 'hồ sơ khách hàng'
    return (
      <div className="space-y-1.5">
        <div className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-2.5 py-1 text-[12px] font-semibold text-primary"><MousePointerClick className="h-3.5 w-3.5" /> {String(cfg.buttonLabel || 'Chạy luồng')}</div>
        <p className="text-[11px] text-muted-foreground">Nút hiện ở {noi}</p>
        {loc.map((r, i) => <div key={i} className="flex items-center gap-1"><span className="text-[10.5px] text-muted-foreground">Chỉ khi</span><LuatChip r={r} /></div>)}
      </div>
    )
  }
  const c = connectors.find((x) => x.id === cfg.connectorId)
  const ev = c ? SOURCE_EVENTS[c.type].find((e) => e.key === cfg.event) : undefined
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">{c && <VongIcon icon={SRC_ICON[c.type]} mau="sky" size="h-7 w-7" />}<div className="min-w-0"><p className="truncate text-[12.5px] font-semibold">{c?.name ?? 'Chọn nguồn ngoài'}</p><p className="truncate text-[11px] text-muted-foreground">{ev?.label ?? 'Chọn sự kiện'}{ev && <span className="font-mono"> · {ev.key}</span>}</p></div></div>
      {c && <p className="text-[10.5px] text-muted-foreground">Cần khối <b>Ánh xạ</b> ngay sau để đổi sang trường CRM.</p>}
    </div>
  )
}

function ThanAnhXa({ d }: { d: BlockData }) {
  const { mappings } = useStoreV2()
  const m = mappings.find((x) => x.id === d.config.mappingId)
  if (!m) return <p className="text-[11px] text-muted-foreground">Chưa chọn bảng ánh xạ</p>
  return (
    <div className="space-y-1">
      <p className="truncate text-[12px] font-semibold">{m.name}</p>
      <div className="rounded-md border bg-muted/40 px-2 py-1 font-mono text-[10.5px] leading-5">
        {m.rules.slice(0, 3).map((r, i) => <div key={i} className="flex items-center gap-1 truncate"><span className="text-muted-foreground">{r.from}</span><span>→</span><span className="text-violet-700 dark:text-violet-300">{r.to}</span></div>)}
        {m.rules.length > 3 && <div className="text-muted-foreground">+{m.rules.length - 3} trường nữa</div>}
      </div>
    </div>
  )
}

function ThanDieuKien({ d }: { d: BlockData }) {
  const rules = (d.config.rules as ConditionRuleV2[]) ?? []
  const or = d.config.logic === 'or'
  if (!rules.length) return <p className="text-[11px] text-muted-foreground">Chưa có luật — bấm khối để thêm</p>
  return (
    <div className="space-y-1">
      {rules.map((r, i) => (
        <div key={i} className="space-y-1">
          {i > 0 && <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{or ? 'hoặc' : 'và'}</p>}
          <LuatChip r={r} />
        </div>
      ))}
    </div>
  )
}

function ThanCho({ d }: { d: BlockData }) {
  if (String(d.config.unit) === 'until_friend') {
    return (
      <div className="space-y-0.5">
        <p className="flex items-center gap-1.5 text-[13px] font-semibold"><UserPlus className="h-3.5 w-3.5 text-primary" /> Chờ khách đồng ý kết bạn</p>
        <p className="text-[11px] text-muted-foreground">Tối đa {String(d.config.value ?? 72)} giờ, đồng ý sớm thì chạy tiếp ngay</p>
      </div>
    )
  }
  const dv = { minutes: 'phút', hours: 'giờ', days: 'ngày' }[String(d.config.unit)] ?? ''
  return <p className="text-[20px] font-bold tabular-nums leading-6">{String(d.config.value ?? 0)} <span className="text-[13px] font-medium text-muted-foreground">{dv}</span><span className="ml-2 text-[11px] font-normal text-muted-foreground">rồi tiếp tục</span></p>
}

function ThanHanhDong({ d }: { d: BlockData }) {
  const cfg = d.config; const t = String(cfg.type ?? 'add_tag') as ActionV2
  const S = (k: string) => String(cfg[k] ?? '')
  if (t === 'send_zalo_oa' && (S('kieuTin') || (S('message') ? 'tu_soan' : 'mau')) === 'mau') {
    return (
      <div className="space-y-1">
        <Dong nhan="Mẫu OA">{S('znsTemplateId') ? `#${S('znsTemplateId')}` : '(chưa chọn mẫu)'}</Dong>
        {S('znsParams') && <div className="rounded-md border bg-muted/40 px-2 py-1 font-mono text-[10.5px] leading-5">{S('znsParams').split('\n').slice(0, 3).map((l, i) => <div key={i} className="truncate"><HienBien text={l} /></div>)}</div>}
        <div className="text-[10.5px] text-muted-foreground">Kênh: <b className="text-foreground">Zalo OA · tin theo mẫu</b></div>
      </div>
    )
  }
  const kenh = t === 'send_zalo_personal' ? 'Zalo cá nhân' : t === 'send_zalo_oa' ? 'Zalo OA' : ({ zalo_oa: 'Zalo OA', zalo_personal: 'Zalo cá nhân', facebook: 'Facebook' }[S('channel')] ?? 'Hội thoại gần nhất')
  switch (t) {
    case 'add_tag': case 'remove_tag':
      return <div className="flex flex-wrap items-center gap-1"><span className="text-[11px] text-muted-foreground">{t === 'add_tag' ? 'Gắn' : 'Gỡ'}</span><Chip mau={t === 'add_tag' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200' : 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-200'}><HienBien text={S('tag') || '(chưa đặt nhãn)'} /></Chip></div>
    case 'send_message': case 'send_zalo_personal': case 'send_zalo_oa': case 'send_survey':
      return (
        <div className="space-y-1.5">
          <KhungTin><HienBien text={S('message') || '(chưa có nội dung)'} /></KhungTin>
          <div className="flex items-center justify-between gap-2 text-[10.5px] text-muted-foreground"><span>Kênh: <b className="text-foreground">{kenh}</b></span>{t === 'send_survey' && <span className="truncate">🔗 {S('formUrl') || 'link form'}</span>}</div>
        </div>
      )
    case 'send_zalo_zns':
      return <div className="space-y-1"><Dong nhan="Template">{S('templateId') || '(chưa có)'}</Dong>{S('params') && <div className="rounded-md border bg-muted/40 px-2 py-1 font-mono text-[10.5px] leading-5">{S('params').split('\n').slice(0, 3).map((l, i) => <div key={i} className="truncate"><HienBien text={l} /></div>)}</div>}</div>
    case 'create_voucher':
      return (
        <div className="flex items-center gap-3">
          <div className="rounded-md border border-dashed border-orange-400 bg-orange-50 px-2.5 py-1.5 text-center dark:bg-orange-500/10"><p className="font-mono text-[12px] font-bold tracking-wide text-orange-700 dark:text-orange-300">{S('prefix') || 'MVS'}-XXXX</p><p className="text-[14px] font-bold text-orange-600">{cfg.kind === 'amount' ? `${Number(cfg.value || 0).toLocaleString('vi-VN')}đ` : `-${S('value') || 0}%`}</p></div>
          <div className="min-w-0 text-[11px] text-muted-foreground"><p>Hạn <b className="text-foreground">{S('expireDays') || 30} ngày</b></p><p className="truncate"><HienBien text={S('note')} /></p><p className="font-mono text-[10px]">→ {'{{voucher.code}}'}</p></div>
        </div>
      )
    case 'create_task':
      return <div className="space-y-1"><KhungTin><HienBien text={S('title') || '(chưa có tiêu đề)'} /></KhungTin><div className="flex justify-between gap-2 text-[10.5px] text-muted-foreground"><span className="truncate">Giao: <b className="text-foreground"><HienBien text={S('assignee') || 'Nhóm CSKH'} /></b></span><span>Hạn {S('dueHours') || 24}h</span></div></div>
    case 'update_property':
      return <Dong nhan={S('key') || 'thuộc tính'}><HienBien text={S('value') || '…'} /></Dong>
    case 'write_sheet':
      return <div className="space-y-1"><Dong nhan="Tab">{S('sheet') || '(chưa đặt)'}</Dong><div className="flex flex-wrap gap-1">{S('columns').split(',').map((c) => c.trim()).filter(Boolean).slice(0, 5).map((c) => <span key={c} className="rounded bg-violet-100 px-1 font-mono text-[10px] text-violet-700 dark:bg-violet-500/25 dark:text-violet-200">{c}</span>)}</div></div>
    case 'http_request':
      return <div className="space-y-1"><div className="flex items-center gap-1.5 text-[11px]"><span className="rounded bg-slate-800 px-1.5 py-0.5 font-mono text-[10px] font-bold text-white">{S('method') || 'POST'}</span><span className="truncate font-mono">{S('url') || 'https://…'}</span></div>{S('body') && <pre className="line-clamp-3 rounded-md border bg-muted/40 px-2 py-1 font-mono text-[10px] leading-4"><HienBien text={S('body')} /></pre>}</div>
    case 'send_notification':
      return <KhungTin><HienBien text={S('message') || '(chưa có nội dung)'} /></KhungTin>
    case 'send_friend_request':
      return (
        <div className="space-y-1">
          <Dong nhan="Gửi tới"><span className="font-mono">{S('phoneField') || 'customer.phone'}</span></Dong>
          <KhungTin><HienBien text={S('message') || 'Nhà thuốc kết bạn để gửi hướng dẫn dùng thuốc ạ.'} /></KhungTin>
        </div>
      )
  }
}

// ── Node ─────────────────────────────────────────────────────────────
const TAB_MAU: Record<NonNullable<BlockData['runStatus']> | 'none', string> = {
  none: 'bg-primary text-primary-foreground', ok: 'bg-success text-white', fail: 'bg-destructive text-white', wait: 'bg-warning text-white', skip: 'bg-muted-foreground/70 text-white',
}

export function KhoiCanvas({ id, data, selected }: NodeProps) {
  const d = data as BlockData
  const hv = useContext(CanvasCtx)
  const K = KHOI_META[d.kind]
  const laKichHoat = d.kind === 'source'
  const mode = String(d.config.mode ?? 'external') as TriggerMode
  const act = d.kind === 'action' ? HANH_DONG_META[String(d.config.type ?? 'add_tag') as ActionV2] : null
  const HeadIcon = laKichHoat ? MODE_ICON[mode] : act ? act.icon : K.icon
  const headMau: Mau = act ? act.mau : K.mau
  const tieuDe = laKichHoat ? TRIGGER_MODE_LABELS[mode].ten : act ? ACTION_V2_LABELS[String(d.config.type) as ActionV2] : K.nhan

  return (
    <div className={cn('relative w-72 pt-3', d.runStatus === 'skip' && 'opacity-60')}>
      {!laKichHoat && <Handle type="target" position={Position.Left} className="!top-[calc(50%+6px)] !h-3 !w-3 !border-2 !border-background !bg-primary" />}
      {/* Nhãn tab */}
      <div className={cn('absolute left-3 top-0 z-10 flex max-w-[calc(100%-1.5rem)] items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold shadow-sm', TAB_MAU[d.runStatus ?? 'none'])}>
        {d.runStatus ? <BieuTuongBuoc status={d.runStatus} className="h-3 w-3 !text-white" /> : <K.icon className="h-3 w-3" />}
        <span className="truncate">{d.label}</span>
        {d.branch && <span className="ml-1 rounded bg-white/25 px-1 text-[10px]">{d.branch === 'true' ? 'Đúng' : 'Sai'}</span>}
      </div>

      <div className={cn('overflow-visible rounded-xl border bg-card shadow-md transition-shadow', selected && 'ring-2 ring-primary/70', d.runStatus === 'fail' && 'border-destructive/60', d.runStatus === 'ok' && 'border-success/50')}>
        {laKichHoat ? (
          <div className="flex items-center gap-2 rounded-t-xl bg-primary px-3 pb-2.5 pt-4 text-primary-foreground">
            <HeadIcon className="h-4 w-4 shrink-0" />
            <p className="min-w-0 flex-1 truncate text-[13px] font-semibold">{tieuDe}</p>
            <Switch className="nodrag" checked={!!hv?.enabled} onCheckedChange={(v) => hv?.toggle(v)} title={hv?.enabled ? 'Luồng đang bật' : 'Luồng đang tắt'} />
          </div>
        ) : (
          <div className="flex items-center gap-2.5 border-b px-3 pb-2 pt-4">
            <VongIcon icon={HeadIcon} mau={headMau} />
            <p className="min-w-0 flex-1 truncate text-[13.5px] font-semibold">{tieuDe}</p>
          </div>
        )}

        <div className="px-3 py-2.5">
          {d.kind === 'source' && <ThanKichHoat d={d} />}
          {d.kind === 'mapping' && <ThanAnhXa d={d} />}
          {d.kind === 'condition' && <ThanDieuKien d={d} />}
          {d.kind === 'delay' && <ThanCho d={d} />}
          {d.kind === 'action' && <ThanHanhDong d={d} />}
        </div>

        {laKichHoat && (
          <div className="border-t px-3 py-2">
            <button type="button" className="nodrag flex w-full items-center justify-center gap-1.5 rounded-md border bg-muted/40 py-1.5 text-[11.5px] font-medium text-primary hover:bg-accent" onClick={(e) => { e.stopPropagation(); hv?.doiKichHoat(id) }}>
              <RefreshCw className="h-3.5 w-3.5" /> Đổi loại kích hoạt
            </button>
          </div>
        )}

        {/* Đầu ra */}
        <div className="flex justify-end gap-1.5 px-2 pb-0 pt-1 -mb-3">
          {d.kind === 'condition' ? (
            <>
              <NutTiepTheo id="true" nhan="Đúng" mau="bg-success" onClick={() => hv?.themSau(id, 'true')} />
              <NutTiepTheo id="false" nhan="Sai" mau="bg-destructive" onClick={() => hv?.themSau(id, 'false')} />
            </>
          ) : <NutTiepTheo nhan="Bước tiếp theo" mau="bg-primary" onClick={() => hv?.themSau(id)} />}
        </div>
      </div>
    </div>
  )
}

// ── Hộp chọn dạng lưới thẻ (như "Chọn loại Trigger") ─────────────────
interface TheChon { key: string; ten: string; moTa: string; icon: LucideIcon; mau: Mau; nhom: string }

function LuoiChon({ nhom, the, onChon, tatCa = 'Tất cả', nhomMacDinh }: { nhom: { key: string; ten: string; icon?: LucideIcon }[]; the: TheChon[]; onChon: (t: TheChon) => void; tatCa?: string; nhomMacDinh?: string }) {
  const [nh, setNh] = useState(nhomMacDinh ?? 'all')
  useEffect(() => { setNh(nhomMacDinh ?? 'all') }, [nhomMacDinh])
  const [q, setQ] = useState('')
  const hien = the.filter((t) => (nh === 'all' || t.nhom === nh) && (!q || `${t.ten} ${t.moTa}`.toLowerCase().includes(q.toLowerCase())))
  return (
    <div className="grid gap-4 sm:grid-cols-[11rem_1fr]">
      <nav className="space-y-0.5">
        {[{ key: 'all', ten: tatCa, icon: LayoutGrid }, ...nhom].map((n) => { const I = n.icon; return (
          <button key={n.key} type="button" onClick={() => setNh(n.key)} className={cn('flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-[13px]', nh === n.key ? 'bg-primary/10 font-semibold text-primary' : 'hover:bg-accent')}>
            {I && <I className="h-4 w-4" />}{n.ten}
          </button>
        ) })}
      </nav>
      <div className="min-w-0 space-y-3">
        <div className="relative"><Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm…" className="h-8 pl-8 text-xs" /></div>
        <div className="grid max-h-[60vh] grid-cols-2 gap-3 overflow-y-auto pr-1 sm:grid-cols-3">
          {hien.map((t) => (
            <button key={t.key} type="button" onClick={() => onChon(t)} className="group flex flex-col items-center gap-2 rounded-xl border bg-card px-3 py-5 text-center transition hover:-translate-y-0.5 hover:border-primary/60 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
              <VongIcon icon={t.icon} mau={t.mau} size="h-14 w-14" />
              <span className="text-[13px] font-semibold">{t.ten}</span>
              <span className="text-[11px] leading-4 text-muted-foreground">{t.moTa}</span>
            </button>
          ))}
          {hien.length === 0 && <p className="col-span-full py-8 text-center text-xs text-muted-foreground">Không có mục phù hợp</p>}
        </div>
      </div>
    </div>
  )
}

/** Chọn loại kích hoạt → trả về patch config cho khối Kích hoạt. */
export function ChonKichHoatDialog({ open, onClose, onChon, nhomMacDinh }: { open: boolean; onClose: () => void; onChon: (patch: Record<string, unknown>) => void; nhomMacDinh?: TriggerMode }) {
  const { connectors } = useStoreV2()
  const the = useMemo<(TheChon & { patch: Record<string, unknown> })[]>(() => [
    ...(Object.keys(INTERNAL_OBJECTS) as InternalObject[]).map((o) => ({ key: `in_${o}`, nhom: 'internal', ten: INTERNAL_OBJECTS[o].ten, moTa: 'Được tạo / sửa / gắn nhãn trong ChatMQL', icon: OBJ_ICON[o], mau: 'blue' as Mau, patch: { mode: 'internal', object: o, internalEvent: 'created', field: '', toValue: '', filterRules: [] } })),
    ...connectors.map((c) => ({ key: `ex_${c.id}`, nhom: 'external', ten: c.name, moTa: `${SOURCE_LABELS[c.type]} · ${c.status === 'connected' ? 'đã kết nối' : 'chưa kết nối'}`, icon: SRC_ICON[c.type], mau: 'sky' as Mau, patch: { mode: 'external', connectorId: c.id, event: SOURCE_EVENTS[c.type][0]?.key ?? '' } })),
    ...Object.entries(LICH_LABELS).map(([cron, ten]) => ({ key: `sch_${cron}`, nhom: 'schedule', ten, moTa: 'Quét bản ghi thoả "Chỉ khi"', icon: CalendarClock, mau: 'indigo' as Mau, patch: { mode: 'schedule', cron, object: 'customer', filterRules: [] } })),
    { key: 'man_customer', nhom: 'manual', ten: 'Nút trên hồ sơ khách', moTa: 'Nhân viên bấm trong CRM', icon: Users, mau: 'teal' as Mau, patch: { mode: 'manual', object: 'customer', buttonLabel: 'Chạy luồng' } },
    { key: 'man_conversation', nhom: 'manual', ten: 'Nút trong hội thoại', moTa: 'Bấm ngay khi đang chat', icon: MessagesSquare, mau: 'teal' as Mau, patch: { mode: 'manual', object: 'conversation', buttonLabel: 'Chạy luồng' } },
    { key: 'man_order', nhom: 'manual', ten: 'Nút trên đơn hàng', moTa: 'Bấm ở chi tiết đơn', icon: ShoppingCart, mau: 'teal' as Mau, patch: { mode: 'manual', object: 'order', buttonLabel: 'Chạy luồng' } },
  ], [connectors])
  const nhom = (Object.keys(TRIGGER_MODE_LABELS) as TriggerMode[]).map((m) => ({ key: m, ten: TRIGGER_MODE_LABELS[m].ten, icon: MODE_ICON[m] }))
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader><DialogTitle>Chọn loại kích hoạt</DialogTitle><DialogDescription>Luồng bắt đầu khi nào. Chi tiết (sự kiện, trường, lọc "Chỉ khi") chỉnh ở bảng bên phải sau khi chọn.</DialogDescription></DialogHeader>
        <LuoiChon nhom={nhom} the={the} tatCa="Tất cả kích hoạt" nhomMacDinh={nhomMacDinh} onChon={(t) => { onChon((t as TheChon & { patch: Record<string, unknown> }).patch); onClose() }} />
      </DialogContent>
    </Dialog>
  )
}

/** Chọn khối tiếp theo (hành động / điều kiện / chờ / ánh xạ). */
export function ChonKhoiDialog({ open, onClose, onChon }: { open: boolean; onClose: () => void; onChon: (kind: NodeKind, cfg: Record<string, unknown>) => void }) {
  const { mappings } = useStoreV2()
  const the = useMemo<(TheChon & { kind: NodeKind; cfg: Record<string, unknown> })[]>(() => [
    ...(Object.keys(HANH_DONG_META) as ActionV2[]).map((a) => ({ key: a, nhom: HANH_DONG_META[a].nhom, ten: ACTION_V2_LABELS[a], moTa: HANH_DONG_META[a].moTa, icon: HANH_DONG_META[a].icon, mau: HANH_DONG_META[a].mau, kind: 'action' as NodeKind, cfg: { type: a, ...(a === 'create_voucher' ? { prefix: 'MVS', value: 10, kind: 'percent', expireDays: 30 } : a === 'create_task' ? { dueHours: 24 } : a === 'http_request' ? { method: 'POST' } : a === 'send_message' || a === 'send_survey' ? { channel: 'zalo_oa' } : {}) } })),
    { key: 'condition', nhom: 'luong', ten: 'Điều kiện', moTa: 'Rẽ nhánh Đúng / Sai', icon: GitBranch, mau: 'amber', kind: 'condition', cfg: { logic: 'and', rules: [{ field: 'order.status', op: 'eq', value: '' }] } },
    { key: 'delay', nhom: 'luong', ten: 'Chờ', moTa: 'Đợi phút / giờ / ngày rồi tiếp', icon: Clock, mau: 'slate', kind: 'delay', cfg: { value: 24, unit: 'hours' } },
    { key: 'mapping', nhom: 'luong', ten: 'Ánh xạ', moTa: 'Đổi dữ liệu ngoài → trường CRM', icon: ArrowLeftRight, mau: 'sky', kind: 'mapping', cfg: { mappingId: mappings[0]?.id ?? '' } },
  ], [mappings])
  const nhom = [
    { key: 'khach', ten: NHOM_LABELS.khach, icon: Users }, { key: 'tin', ten: NHOM_LABELS.tin, icon: MessageCircle }, { key: 'noibo', ten: NHOM_LABELS.noibo, icon: Bell },
    { key: 'tichhop', ten: NHOM_LABELS.tichhop, icon: Globe }, { key: 'luong', ten: NHOM_LABELS.luong, icon: GitBranch },
  ]
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader><DialogTitle>Chọn khối tiếp theo</DialogTitle><DialogDescription>Khối mới được nối ngay sau khối đang chọn.</DialogDescription></DialogHeader>
        <LuoiChon nhom={nhom} the={the} tatCa="Tất cả khối" onChon={(t) => { const x = t as TheChon & { kind: NodeKind; cfg: Record<string, unknown> }; onChon(x.kind, x.cfg); onClose() }} />
      </DialogContent>
    </Dialog>
  )
}

/** Thư viện mẫu luồng cho nhà thuốc OTC — chọn một mẫu là áp thẳng lên canvas. */
export function ChonMauDialog({ open, onClose, onChon, coKhoi }: { open: boolean; onClose: () => void; onChon: (mau: MauWorkflow) => void; coKhoi: number }) {
  const [nh, setNh] = useState<NhomMau | 'all'>('all')
  const [q, setQ] = useState('')
  const hien = MAU_WORKFLOW.filter((m) => (nh === 'all' || m.nhom === nh) && (!q || `${m.ten} ${m.moTa} ${m.khiNao}`.toLowerCase().includes(q.toLowerCase())))
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Mẫu luồng cho nhà thuốc</DialogTitle>
          <DialogDescription>Chọn một mẫu để áp lên canvas{coKhoi > 1 ? ' — các khối đang có sẽ được thay thế' : ''}. Sau đó sửa nội dung tin, nhãn, thời gian chờ rồi bấm Lưu.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-[13rem_1fr]">
          <nav className="space-y-0.5">
            {([['all', 'Tất cả mẫu'], ...(Object.keys(NHOM_MAU) as NhomMau[]).map((k) => [k, NHOM_MAU[k].ten] as const)] as Array<readonly [string, string]>).map(([k, ten]) => (
              <button key={k} type="button" onClick={() => setNh(k as NhomMau | 'all')} className={cn('w-full rounded-md px-3 py-2 text-left text-[13px]', nh === k ? 'bg-primary/10 font-semibold text-primary' : 'hover:bg-accent')}>
                {ten}
                <span className="ml-1 text-[11px] font-normal text-muted-foreground">{k === 'all' ? MAU_WORKFLOW.length : MAU_WORKFLOW.filter((m) => m.nhom === k).length}</span>
              </button>
            ))}
          </nav>
          <div className="min-w-0 space-y-3">
            <div className="relative"><Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm mẫu…" className="h-8 pl-8 text-xs" /></div>
            <div className="grid max-h-[62vh] grid-cols-1 gap-3 overflow-y-auto pr-1 lg:grid-cols-2">
              {hien.map((m) => (
                <button key={m.id} type="button" onClick={() => { onChon(m); onClose() }} className="group flex flex-col gap-1.5 rounded-xl border bg-card p-3 text-left transition hover:-translate-y-0.5 hover:border-primary/60 hover:shadow-md">
                  <div className="flex items-start gap-2">
                    <VongIcon icon={Workflow} mau="blue" size="h-8 w-8" />
                    <div className="min-w-0"><p className="text-[13.5px] font-semibold leading-tight">{m.ten}</p><p className="text-[11px] text-muted-foreground">{NHOM_MAU[m.nhom].ten} · {m.nodes.length} khối</p></div>
                  </div>
                  <p className="text-[11.5px] leading-4 text-muted-foreground">{m.moTa}</p>
                  <div className="mt-auto space-y-0.5 pt-1 text-[11px]">
                    <p className="flex gap-1.5"><span className="shrink-0 text-muted-foreground">Khi nào:</span><span className="min-w-0 flex-1">{m.khiNao}</span></p>
                    <p className="flex gap-1.5"><span className="shrink-0 text-muted-foreground">Kết quả:</span><span className="min-w-0 flex-1">{m.ketQua}</span></p>
                  </div>
                </button>
              ))}
              {hien.length === 0 && <p className="col-span-full py-8 text-center text-xs text-muted-foreground">Không có mẫu phù hợp</p>}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
